import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, Send, Mic, Paperclip, LogOut, Loader, FileText, Download, Check, RefreshCw, ClipboardList, Calendar } from 'lucide-react';
import SCENARIOS from '../../data/adHocChatData';
import { emitRipple } from '../../hooks/rippleBus';
import { scaleDelay } from '../../hooks/demoSpeed';

const AGENT_THUMBS = {
  luc: '/Thumbnails/Luc.png', dora: '/Thumbnails/Dora.png', finn: '/Thumbnails/Finn.png',
  marsi: '/Thumbnails/Marsi.png', tali: '/Thumbnails/Tali.png', stark: '/Thumbnails/Stark.png',
  sally: '/Thumbnails/Sally.png', wright: '/Thumbnails/Wright.png', buzz: '/Thumbnails/Buzz.png',
  zira: '/Thumbnails/Ziira.png',
};
const AGENT_NAMES = {
  luc: 'Luc', dora: 'Dora', finn: 'Finn', marsi: 'Marsi',
  tali: 'Tali', stark: 'Stark', sally: 'Sally', wright: 'Wright', buzz: 'Buzz', zira: 'Zira',
};
const AGENT_ROLES = {
  luc: 'Personal Growth Partner', dora: 'Research & Data Analyst', finn: 'Finance Expert',
  marsi: 'Marketing & Growth', tali: "HR & People's Partner", stark: 'Business Strategist',
  sally: 'Sales Expert', wright: 'Copywriting Expert', buzz: 'Social Media Expert',
  zira: 'Support Expert',
};

/* ─── Rich text renderer — parses **bold** and inline `code` markers ─── */
const renderRichText = (text) => {
  if (text == null) return null;
  const str = String(text);
  // Match **bold** segments and split
  const parts = str.split(/(\*\*[^*\n]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

/* ─── Per-message error boundary — prevents one bad event from blanking the whole chat ─── */
class MessageBoundary extends React.Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err, info) {
    // Non-fatal — scenarios occasionally ship with shape drift; log for dev visibility.
    // eslint-disable-next-line no-console
    console.warn('[V4AdHocChat] Message render failed, swallowed:', err?.message, this.props.msgType);
  }
  render() {
    if (this.state.failed) {
      // Silent skip — keeps the rest of the transcript alive.
      return null;
    }
    return this.props.children;
  }
}

/* ─── Single message renderer (shared by both single & multi-column) ─── */
const ColumnMessage = ({ msg, isDark, isMultiColumn, columnCount, onCtaClick }) => {
  const isUser = msg.type === 'user';
  const isContext = msg.type === 'context';
  const isLeave = msg.type === 'leave';
  const isCta = msg.type === 'cta';
  const isDeckProgress = msg.type === 'deck-progress';
  const isDeckPreview = msg.type === 'deck-preview';
  const isTaskCard = msg.type === 'task-card';
  const isCalendarCard = msg.type === 'calendar-card';
  const isEmailDraft = msg.type === 'email-draft';
  const isSwotCard = msg.type === 'swot-card';
  const isSourceLink = msg.type === 'source-link';
  const isThinkingPause = msg.type === 'thinking-pause';
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const divider = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';

  // Font sizes: single=14px, 2-col=13px, 3+=12px
  const multiSize = (columnCount || 2) >= 3 ? 'text-[12px] leading-[1.75]' : 'text-[13px] leading-[1.75]';
  const agentTextSize = isMultiColumn ? multiSize : 'text-[14px] leading-[1.8]';
  const userTextSize = isMultiColumn ? multiSize : 'text-[14px] leading-[1.8]';
  const contextTextSize = isMultiColumn ? 'text-[11px] leading-[1.7]' : 'text-[13px] leading-[1.7]';
  const avatarSize = isMultiColumn ? 'w-6 h-6' : 'w-8 h-8';
  const paragraphGap = isMultiColumn ? 10 : 16;

  if (isLeave) {
    return (
      <div className={`flex items-center justify-center gap-2 py-3 text-[11px] ${isDark ? 'text-white/30' : 'text-slate-400'}`} style={{ animation: 'adHocFade 0.3s ease-out' }}>
        <LogOut className="w-3 h-3" />
        <span>{msg.text}</span>
      </div>
    );
  }

  if (isCta) {
    const dismissed = msg.dismissed;
    return (
      <div className="flex items-center gap-3 py-3" style={{ animation: 'adHocFade 0.3s ease-out', marginTop: isMultiColumn ? 16 : 24, opacity: dismissed ? 0.5 : 1 }}>
        <span className={`text-[13px] ${textSecondary}`}>Luc suggests looping in {AGENT_NAMES[msg.target]}</span>
        <button
          onClick={() => !dismissed && onCtaClick && onCtaClick(msg.target, 'loop')}
          disabled={dismissed}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-[12px] font-medium bg-gradient-to-r from-indigo-500 to-purple-600 text-white transition-transform ${dismissed ? 'cursor-not-allowed' : 'hover:scale-[1.02] cursor-pointer'}`}
          data-testid={`cta-loop-${msg.target}`}
        >
          {msg.ctaLabel}
        </button>
        <button
          onClick={() => !dismissed && onCtaClick && onCtaClick(msg.target, 'loop')}
          disabled={dismissed}
          className={`px-4 py-2 rounded-xl text-[12px] font-medium border ${divider} ${textSecondary} transition-colors ${dismissed ? 'cursor-not-allowed' : 'hover:bg-black/[0.04] cursor-pointer'}`}
          data-testid={`cta-continue-${msg.target}`}
        >
          Continue as is
        </button>
      </div>
    );
  }

  if (isDeckProgress) {
    return (
      <div style={{ marginTop: isMultiColumn ? 16 : 32, animation: 'adHocFade 0.4s ease-out' }}>
        <div className="flex items-start gap-3.5">
          <img src={AGENT_THUMBS[msg.agent]} alt="" className={`${avatarSize} rounded-full object-cover flex-shrink-0 mt-0.5`} />
          <div className={`flex-1 rounded-xl px-5 py-4 ${isDark ? 'bg-indigo-500/8 border border-indigo-500/10' : 'bg-indigo-50 border border-indigo-200/40'}`}>
            <div className="space-y-2.5">
              {msg.steps.map((step, i) => (
                <div key={i} className="flex items-center gap-2.5" style={{ animation: `adHocFade 0.3s ease-out ${i * 0.15}s both` }}>
                  <Check className={`w-3.5 h-3.5 flex-shrink-0 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                  <span className={`${isMultiColumn ? 'text-[11px]' : 'text-[12px]'} ${isDark ? 'text-white/60' : 'text-slate-600'}`}>{step}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isDeckPreview) {
    const deck = msg.deck || {};
    const slides = Array.isArray(deck.slides) ? deck.slides : [];
    const slideCount = typeof deck.slideCount === 'number' ? deck.slideCount : slides.length;
    return (
      <div style={{ marginTop: isMultiColumn ? 16 : 32, animation: 'adHocFade 0.5s ease-out' }}>
        <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-white/[0.02] border-white/[0.08]' : 'bg-white/80 border-black/[0.06]'}`}>
          <div className={`px-5 py-4 border-b ${isDark ? 'border-white/[0.06] bg-gradient-to-r from-indigo-500/10 to-purple-500/10' : 'border-black/[0.04] bg-gradient-to-r from-indigo-50 to-purple-50'}`}>
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isDark ? 'bg-indigo-500/20' : 'bg-indigo-500/10'}`}>
                <FileText className={`w-4.5 h-4.5 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-[13px] font-semibold ${textPrimary}`}>{renderRichText(deck.title || 'Deck preview')}</p>
                <p className={`text-[10px] ${textSecondary}`}>{deck.subtitle ? `${deck.subtitle} · ` : ''}{slideCount} slides</p>
              </div>
              <button className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium ${isDark ? 'bg-white/[0.06] text-white/70 hover:bg-white/[0.1]' : 'bg-black/[0.04] text-slate-600 hover:bg-black/[0.08]'} transition-colors`} data-testid="deck-download">
                <Download className="w-3 h-3" /> Download
              </button>
            </div>
          </div>
          <div className="px-5 py-3">
            <div className="space-y-1.5">
              {slides.map((slide, idx) => (
                <div key={slide.num ?? idx} className={`flex items-start gap-3 py-1.5 ${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-black/[0.01]'} rounded-lg px-2 -mx-2 transition-colors`}>
                  <span className={`text-[10px] font-mono w-5 text-right flex-shrink-0 mt-0.5 ${isDark ? 'text-white/25' : 'text-slate-400'}`}>{slide.num ?? idx + 1}</span>
                  <div className="min-w-0">
                    <p className={`text-[12px] font-medium ${textPrimary}`}>{renderRichText(slide.title)}</p>
                    <p className={`text-[10px] ${textSecondary}`}>{renderRichText(slide.detail || slide.body)}</p>
                  </div>
                </div>
              ))}
            </div>
            {slideCount > slides.length && (
              <p className={`text-[10px] mt-2 pl-8 ${textSecondary}`}>+ {slideCount - slides.length} more slides</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (isTaskCard) {
    const card = msg.card || {};
    const isRecurring = card.type === 'recurring-task';
    const items = Array.isArray(card.items) ? card.items : [];
    return (
      <div style={{ marginTop: isMultiColumn ? 16 : 32, animation: 'adHocFade 0.5s ease-out' }}>
        <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-white/[0.02] border-white/[0.08]' : 'bg-white/80 border-black/[0.06]'}`}>
          <div className={`px-5 py-3.5 border-b ${isDark ? 'border-white/[0.06]' : 'border-black/[0.04]'} flex items-center gap-3`}>
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isRecurring ? (isDark ? 'bg-indigo-500/20' : 'bg-indigo-500/10') : (isDark ? 'bg-emerald-500/20' : 'bg-emerald-500/10')}`}>
              {isRecurring ? <RefreshCw className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} /> : <ClipboardList className={`w-4 h-4 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-[12px] font-semibold ${textPrimary}`}>{renderRichText(card.title)}</p>
              <p className={`text-[10px] ${textSecondary}`}>
                {isRecurring ? card.recurrence : `Assigned to ${card.assignee} · ${card.assigneeRole}`}
              </p>
            </div>
            <span className={`text-[9px] font-medium px-2 py-0.5 rounded-full ${
              card.priority === 'Urgent' ? (isDark ? 'bg-red-400/10 text-red-400' : 'bg-red-600/10 text-red-600')
              : card.status === 'Scheduled' ? (isDark ? 'bg-indigo-400/10 text-indigo-400' : 'bg-indigo-600/10 text-indigo-600')
              : (isDark ? 'bg-emerald-400/10 text-emerald-400' : 'bg-emerald-600/10 text-emerald-600')
            }`}>{card.priority || card.status}</span>
          </div>
          <div className="px-5 py-3 space-y-2">
            {items.map((item, i) => (
              <div key={i} className="flex items-center justify-between gap-3">
                <span className={`text-[11px] ${textSecondary}`}>{renderRichText(item.label)}</span>
                <div className="flex items-center gap-1.5">
                  <span className={`text-[11px] font-medium ${textPrimary}`}>{renderRichText(item.value)}</span>
                  {item.status === 'done' && <Check className={`w-3 h-3 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (isCalendarCard) {
    const card = msg.card || {};
    // Tolerate both shapes — { date, agenda:[] } OR { day, context:string }
    const whenLabel = card.date || card.day || '';
    const attendees = Array.isArray(card.attendees) ? card.attendees : (card.attendees ? [card.attendees] : []);
    const agendaItems = Array.isArray(card.agenda)
      ? card.agenda
      : (typeof card.agenda === 'string' && card.agenda.trim() ? [card.agenda] : []);
    const contextText = typeof card.context === 'string' ? card.context : (card.context ? String(card.context) : '');
    return (
      <div style={{ marginTop: isMultiColumn ? 16 : 32, animation: 'adHocFade 0.5s ease-out' }}>
        <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-white/[0.02] border-white/[0.08]' : 'bg-white/80 border-black/[0.06]'}`}>
          <div className={`px-5 py-3.5 border-b ${isDark ? 'border-white/[0.06]' : 'border-black/[0.04]'} flex items-center gap-3`}>
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? 'bg-blue-500/20' : 'bg-blue-500/10'}`}>
              <Calendar className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-[12px] font-semibold ${textPrimary}`}>{renderRichText(card.title || 'Calendar invite')}</p>
              <p className={`text-[10px] ${textSecondary}`}>{whenLabel}{whenLabel && card.time ? ' · ' : ''}{card.time || ''}</p>
            </div>
            <span className={`text-[9px] font-medium px-2 py-0.5 rounded-full ${isDark ? 'bg-blue-400/10 text-blue-400' : 'bg-blue-600/10 text-blue-600'}`}>Invite Sent</span>
          </div>
          <div className="px-5 py-3 space-y-2.5">
            {attendees.length > 0 && (
              <div>
                <p className={`text-[10px] font-medium mb-1 ${textSecondary}`}>Attendees</p>
                <div className="flex flex-wrap gap-1.5">
                  {attendees.map((a, i) => (
                    <span key={i} className={`text-[10px] px-2 py-0.5 rounded-full ${isDark ? 'bg-white/[0.06] text-white/60' : 'bg-black/[0.04] text-slate-600'}`}>{a}</span>
                  ))}
                </div>
              </div>
            )}
            {agendaItems.length > 0 && (
              <div>
                <p className={`text-[10px] font-medium mb-1 ${textSecondary}`}>Agenda</p>
                <div className="space-y-1">
                  {agendaItems.map((item, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className={`text-[10px] mt-0.5 ${isDark ? 'text-white/25' : 'text-slate-400'}`}>{i + 1}.</span>
                      <span className={`text-[11px] ${isDark ? 'text-white/70' : 'text-slate-700'}`}>{renderRichText(item)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {agendaItems.length === 0 && contextText && (
              <div>
                <p className={`text-[10px] font-medium mb-1 ${textSecondary}`}>Context</p>
                <p className={`text-[11px] leading-relaxed ${isDark ? 'text-white/70' : 'text-slate-700'}`}>{renderRichText(contextText)}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (isEmailDraft) {
    const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.05]';
    return (
      <div style={{ marginTop: isMultiColumn ? 16 : 32, animation: 'adHocFade 0.5s ease-out' }}>
        <div className={`px-7 py-6 rounded-xl border ${isDark ? 'bg-white/[0.02] border-white/[0.06]' : 'bg-white/70 border-black/[0.05]'}`}>
          <div className={`text-[14px] leading-[1.8] ${isDark ? 'text-white/80' : 'text-slate-700'}`}>
            {msg.text.split('\n').map((line, i) => {
              if (line.trim() === '') return <div key={i} style={{ height: '12px' }} />;
              return <p key={i} style={{ marginBottom: 4 }}>{renderRichText(line)}</p>;
            })}
          </div>
        </div>
        {msg.label && (
          <div className={`flex items-center gap-1.5 mt-2`}>
            <Check className={`w-3.5 h-3.5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
            <span className={`text-[11px] font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{renderRichText(msg.label)}</span>
          </div>
        )}
      </div>
    );
  }

  if (isThinkingPause) {
    return (
      <div style={{ marginTop: isMultiColumn ? 12 : 24, animation: 'adHocFade 0.3s ease-out' }}>
        <div className="flex items-center gap-3">
          <img src={AGENT_THUMBS[msg.agent]} alt="" className={`${avatarSize} rounded-full object-cover flex-shrink-0`} />
          <div className="flex items-center gap-2">
            <span className={`text-[12px] italic ${isDark ? 'text-white/40' : 'text-slate-400'}`}>{msg.text ? renderRichText(msg.text) : `${AGENT_NAMES[msg.agent]} is thinking...`}</span>
            <span className="flex gap-0.5">
              <span className={`w-1 h-1 rounded-full ${isDark ? 'bg-white/30' : 'bg-slate-400'} animate-bounce`} style={{ animationDelay: '0ms' }} />
              <span className={`w-1 h-1 rounded-full ${isDark ? 'bg-white/30' : 'bg-slate-400'} animate-bounce`} style={{ animationDelay: '150ms' }} />
              <span className={`w-1 h-1 rounded-full ${isDark ? 'bg-white/30' : 'bg-slate-400'} animate-bounce`} style={{ animationDelay: '300ms' }} />
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (isSourceLink) {
    return (
      <div style={{ marginTop: isMultiColumn ? 8 : 16, animation: 'adHocFade 0.3s ease-out' }}>
        <div className="ml-[52px]">
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg ${isDark ? 'bg-white/[0.04] hover:bg-white/[0.06]' : 'bg-black/[0.03] hover:bg-black/[0.05]'} transition-colors cursor-pointer`}>
            <Paperclip className={`w-3 h-3 ${isDark ? 'text-white/30' : 'text-slate-400'}`} />
            <span className={`text-[11px] ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>{renderRichText(msg.title)}</span>
            <span className={`text-[10px] ${isDark ? 'text-white/25' : 'text-slate-400'}`}>{msg.source}</span>
          </div>
        </div>
      </div>
    );
  }

  if (isSwotCard) {
    const QUADRANT_STYLES = {
      strengths: { label: 'Strengths', color: isDark ? 'text-emerald-400' : 'text-emerald-600', bg: isDark ? 'bg-emerald-500/5 border-emerald-500/10' : 'bg-emerald-50/60 border-emerald-200/40' },
      weaknesses: { label: 'Weaknesses', color: isDark ? 'text-red-400' : 'text-red-500', bg: isDark ? 'bg-red-500/5 border-red-500/10' : 'bg-red-50/60 border-red-200/40' },
      opportunities: { label: 'Opportunities', color: isDark ? 'text-indigo-400' : 'text-indigo-600', bg: isDark ? 'bg-indigo-500/5 border-indigo-500/10' : 'bg-indigo-50/60 border-indigo-200/40' },
      threats: { label: 'Threats', color: isDark ? 'text-amber-400' : 'text-amber-600', bg: isDark ? 'bg-amber-500/5 border-amber-500/10' : 'bg-amber-50/60 border-amber-200/40' },
    };
    return (
      <div style={{ marginTop: isMultiColumn ? 16 : 32, animation: 'adHocFade 0.5s ease-out' }}>
        <div className="grid grid-cols-2 gap-2.5">
          {['strengths', 'weaknesses', 'opportunities', 'threats'].map(key => {
            const style = QUADRANT_STYLES[key];
            const items = msg[key] || [];
            return (
              <div key={key} className={`rounded-xl border px-4 py-3 ${style.bg}`}>
                <p className={`text-[10px] font-semibold uppercase tracking-wider mb-2 ${style.color}`}>{style.label}</p>
                <ul className="space-y-1">
                  {items.map((item, i) => (
                    <li key={i} className={`text-[11px] leading-relaxed ${textPrimary}`}>{'\u2022'} {renderRichText(item)}</li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    );
  }



  if (isContext) {
    return (
      <div style={{ marginTop: isMultiColumn ? 16 : 32, animation: 'adHocFade 0.35s ease-out' }}>
        <div className="flex items-start gap-3">
          <img src={AGENT_THUMBS[msg.from]} alt="" className={`${avatarSize} rounded-full object-cover flex-shrink-0 mt-0.5`} />
          <div className={`${contextTextSize} italic ${isDark ? 'text-indigo-400/70' : 'text-indigo-500/70'}`}>
            {renderRichText(msg.text)}
          </div>
        </div>
      </div>
    );
  }

  if (isUser) {
    return (
      <div style={{ marginTop: isMultiColumn ? 16 : 32, animation: 'adHocFade 0.3s ease-out' }}>
        <div className="flex justify-end">
          <div className={`max-w-[85%] px-5 py-3.5 rounded-2xl rounded-br-md ${userTextSize} ${isDark ? 'bg-indigo-500/20 text-white' : 'bg-indigo-100 border border-indigo-200/80 text-slate-800'}`}>
            {renderRichText(msg.text)}
          </div>
        </div>
      </div>
    );
  }

  // Agent message
  return (
    <div style={{ marginTop: isMultiColumn ? 16 : 32, animation: 'adHocFade 0.4s ease-out' }}>
      <div className="flex items-start gap-3.5">
        <img src={AGENT_THUMBS[msg.agent]} alt="" className={`${avatarSize} rounded-full object-cover flex-shrink-0 mt-0.5`} />
        <div className={`${agentTextSize} ${isDark ? 'text-white/85' : 'text-slate-700'}`}>
          {msg.text.split('\n').filter(Boolean).map((p, i, arr) => (
            <p key={i} style={{ marginBottom: i < arr.length - 1 ? paragraphGap : 0 }}>{renderRichText(p)}</p>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─── Multi-column card wrapper (only used when 2+ agents) ─── */
const AgentCard = ({ agent, messages, isDark, isThinking, isActiveChat, columnCount, onCtaClick }) => {
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const scrollRef = useRef(null);
  const hasMessages = messages.length > 0;

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <div
      className={`flex flex-col min-h-0 rounded-xl border ${isActiveChat ? (isDark ? 'border-indigo-500/30' : 'border-indigo-400/40') : border} transition-colors overflow-hidden`}
      data-testid={`adhoc-col-${agent}`}
    >
      {/* Card header */}
      <div className={`flex items-center gap-2 px-3 py-2.5 flex-shrink-0 border-b ${border} ${isDark ? 'bg-white/[0.02]' : 'bg-black/[0.01]'}`}>
        <img src={AGENT_THUMBS[agent]} alt="" className="w-6 h-6 rounded-full object-cover" />
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-semibold ${textPrimary}`}>{AGENT_NAMES[agent]}</p>
          <p className={`text-[9px] ${textSecondary}`}>{AGENT_ROLES[agent]}</p>
        </div>
        <div className="flex items-center gap-1.5">
          {isThinking ? (
            <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-blue-400/10 text-blue-400' : 'bg-blue-600/10 text-blue-600'}`}>
              <Loader className="w-2.5 h-2.5 animate-spin inline mr-0.5" /> Thinking
            </span>
          ) : hasMessages ? (
            <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-emerald-400/10 text-emerald-400' : 'bg-emerald-600/10 text-emerald-600'}`}>Active</span>
          ) : (
            <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-white/[0.06] text-white/40' : 'bg-black/[0.04] text-slate-500'}`}>Waiting</span>
          )}
        </div>
      </div>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-2">
        {messages.map((msg, i) => (
          <MessageBoundary key={i} msgType={msg?.type}>
            <ColumnMessage msg={msg} isDark={isDark} isMultiColumn={true} columnCount={columnCount} onCtaClick={onCtaClick} />
          </MessageBoundary>
        ))}
        <div ref={scrollRef} />
      </div>
    </div>
  );
};

/* ─── Main component ─── */
const V4AdHocChat = ({ isDark, scenarioId, onBack, scenarios: externalScenarios, backLabel, skipFirstUserDelay = false }) => {
  const allScenarios = externalScenarios || SCENARIOS;
  const scenario = allScenarios[scenarioId];
  const [columns, setColumns] = useState([]);
  const [columnMessages, setColumnMessages] = useState({});
  const [eventIndex, setEventIndex] = useState(0);
  const [showModal, setShowModal] = useState(null);
  const [thinking, setThinking] = useState(null);
  const [activeAgent, setActiveAgent] = useState('luc');
  const [messageCount, setMessageCount] = useState(0);
  const timerRefs = useRef([]);
  const processedRef = useRef(new Set());
  const singleScrollRef = useRef(null);

  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const divider = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';
  const solidBg = isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]';

  const showTopicTitle = messageCount >= 3;
  const headerTitle = showTopicTitle ? scenario?.topic : `New Chat with ${AGENT_NAMES[scenario?.initialAgent]}`;
  const isSingleColumn = columns.length === 1;

  const addTimer = useCallback((cb, delay) => {
    const id = setTimeout(cb, scaleDelay(delay));
    timerRefs.current.push(id);
  }, []);

  const addMsg = useCallback((agent, msg) => {
    setColumnMessages(prev => ({
      ...prev,
      [agent]: [...(prev[agent] || []), msg],
    }));
    if (msg.type === 'user' || msg.type === 'agent') {
      setMessageCount(prev => prev + 1);
    }
  }, []);

  // Handle user clicking CTA — clears pending timers, marks CTA as dismissed,
  // and immediately advances the scenario to trigger the agent-join flow.
  const handleCtaClick = useCallback((target) => {
    // Clear all pending timers so auto-play doesn't race the user's click
    timerRefs.current.forEach(clearTimeout);
    timerRefs.current = [];
    // Mark all matching CTAs as dismissed (keeps them visible but disabled)
    setColumnMessages(prev => {
      const nextMessages = {};
      for (const [agent, msgs] of Object.entries(prev)) {
        nextMessages[agent] = msgs.map(m =>
          m.type === 'cta' && m.target === target ? { ...m, dismissed: true } : m
        );
      }
      return nextMessages;
    });
    // Advance event index immediately to fire the next event (usually explicit-loop / agent-join)
    setEventIndex(prev => prev + 1);
  }, []);

  // Auto-scroll single-column view
  useEffect(() => {
    if (isSingleColumn) {
      singleScrollRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [columnMessages, isSingleColumn]);

  const processEventRef = useRef(null);
  processEventRef.current = (idx) => {
    if (!scenario || idx >= scenario.events.length) return;
    if (processedRef.current.has(idx)) return;
    processedRef.current.add(idx);

    const ev = scenario.events[idx];
    const next = () => setEventIndex(prev => prev + 1);

    switch (ev.type) {
      case 'user-msg': {
        // If the prompt was already typed into the page's chat input before the panel opened,
        // skip the initial thinking delay — the message should appear near-instant.
        const preDelay = (idx === 0 && skipFirstUserDelay) ? 80 : 1500;
        const postDelay = (idx === 0 && skipFirstUserDelay) ? 350 : 800;
        addTimer(() => {
          addMsg(ev.column, { type: 'user', text: ev.text });
          addTimer(next, postDelay);
        }, preDelay);
        break;
      }
      case 'agent-msg': {
        setThinking(ev.agent);
        addTimer(() => {
          setThinking(null);
          addMsg(ev.agent, { type: 'agent', agent: ev.agent, text: ev.text });
          addTimer(next, 600);
        }, 2200);
        break;
      }
      case 'suggest-loop': {
        // Show CTA inline in the primary agent's column
        addTimer(() => {
          const primaryAgent = scenario.initialAgent || columns[0] || 'luc';
          addMsg(primaryAgent, { type: 'cta', target: ev.target, ctaLabel: ev.ctaLabel });
          // Auto-advance after 2.5s
          addTimer(next, 2500);
        }, 1000);
        break;
      }
      case 'explicit-loop': {
        addTimer(next, 800);
        break;
      }
      case 'selector-click': {
        addTimer(() => {
          setShowModal(ev.agent);
          addTimer(() => {
            setShowModal(null);
            next();
          }, 2500);
        }, 1000);
        break;
      }
      case 'agent-join': {
        addTimer(() => {
          // Remove CTA messages for this agent from the primary agent's column
          const primaryAgent = scenario.initialAgent || columns[0] || 'luc';
          setColumnMessages(prev => ({
            ...prev,
            [primaryAgent]: (prev[primaryAgent] || []).filter(m => !(m.type === 'cta' && m.target === ev.agent)),
            [ev.agent]: [],
          }));
          setColumns(prev => [...prev, ev.agent]);
          setActiveAgent(ev.agent);
          addTimer(() => {
            if (ev.contextText) {
              addMsg(ev.agent, { type: 'context', from: primaryAgent, text: ev.contextText });
            }
            if (ev.selfIntro) {
              addMsg(ev.agent, { type: 'agent', agent: ev.agent, text: ev.selfIntro });
            }
            addTimer(next, 1000);
          }, 800);
        }, 500);
        break;
      }
      case 'agent-leave': {
        addTimer(() => {
          addMsg(ev.agent, { type: 'leave', text: ev.text });
          addTimer(() => {
            setColumns(prev => prev.filter(a => a !== ev.agent));
            next();
          }, 4000);
        }, 500);
        break;
      }
      case 'deck-progress': {
        // Stagger the building steps, then advance
        const steps = ev.steps || [];
        let stepDelay = 800;
        // Show steps one-by-one by accumulating them into a single message
        const revealSteps = (idx) => {
          if (idx >= steps.length) {
            addTimer(next, 800);
            return;
          }
          addTimer(() => {
            setThinking(ev.agent);
            addTimer(() => {
              setThinking(null);
              // Replace the progress message with accumulated steps
              setColumnMessages(prev => {
                const msgs = prev[ev.agent] || [];
                const existing = msgs.findIndex(m => m.type === 'deck-progress');
                const newSteps = steps.slice(0, idx + 1);
                const progressMsg = { type: 'deck-progress', agent: ev.agent, steps: newSteps };
                if (existing >= 0) {
                  const updated = [...msgs];
                  updated[existing] = progressMsg;
                  return { ...prev, [ev.agent]: updated };
                }
                return { ...prev, [ev.agent]: [...msgs, progressMsg] };
              });
              revealSteps(idx + 1);
            }, 1200);
          }, stepDelay);
        };
        revealSteps(0);
        break;
      }
      case 'deck-preview': {
        addTimer(() => {
          addMsg(ev.agent, { type: 'deck-preview', agent: ev.agent, deck: ev.deck || ev.card });
          if (ev.ripple) {
            const ripples = Array.isArray(ev.ripple) ? ev.ripple : [ev.ripple];
            ripples.forEach((r) => {
              if (r?.to && r?.kind && r?.payload) emitRipple(r.to, r.kind, r.payload);
            });
          }
          addTimer(next, 600);
        }, 1000);
        break;
      }
      case 'task-card': {
        addTimer(() => {
          addMsg(ev.agent, { type: 'task-card', card: ev.card });
          // If scenario attaches a ripple, dispatch to cross-account inbox via the bus
          if (ev.ripple) {
            const ripples = Array.isArray(ev.ripple) ? ev.ripple : [ev.ripple];
            ripples.forEach((r) => {
              if (r?.to && r?.kind && r?.payload) emitRipple(r.to, r.kind, r.payload);
            });
          }
          addTimer(next, 1200);
        }, 800);
        break;
      }
      case 'calendar-card': {
        addTimer(() => {
          addMsg(ev.agent, { type: 'calendar-card', card: ev.card });
          addTimer(next, 1200);
        }, 800);
        break;
      }
      case 'email-draft': {
        addTimer(() => {
          addMsg(ev.agent, { type: 'email-draft', text: ev.text, label: ev.label });
          if (ev.ripple) {
            const ripples = Array.isArray(ev.ripple) ? ev.ripple : [ev.ripple];
            ripples.forEach((r) => {
              if (r?.to && r?.kind && r?.payload) emitRipple(r.to, r.kind, r.payload);
            });
          }
          addTimer(next, 1000);
        }, 800);
        break;
      }
      case 'thinking-pause': {
        // Show thinking indicator, then replace it with next event
        addTimer(() => {
          addMsg(ev.agent, { type: 'thinking-pause', agent: ev.agent, text: ev.text });
          addTimer(() => {
            // Remove the thinking-pause message and advance
            setColumnMessages(prev => {
              const msgs = prev[ev.agent] || [];
              return { ...prev, [ev.agent]: msgs.filter(m => m.type !== 'thinking-pause') };
            });
            next();
          }, 1800);
        }, 300);
        break;
      }
      case 'source-link': {
        addTimer(() => {
          addMsg(ev.agent, { type: 'source-link', title: ev.title || ev.label, source: ev.source || ev.url });
          addTimer(next, 500);
        }, 200);
        break;
      }
      case 'swot-card': {
        addTimer(() => {
          addMsg(ev.agent, {
            type: 'swot-card',
            strengths: ev.strengths,
            weaknesses: ev.weaknesses,
            opportunities: ev.opportunities,
            threats: ev.threats,
          });
          addTimer(next, 800);
        }, 600);
        break;
      }
      default:
        next();
    }
  };

  // Initialize
  useEffect(() => {
    if (!scenario) return;
    timerRefs.current.forEach(clearTimeout);
    timerRefs.current = [];
    processedRef.current = new Set();
    setColumns([scenario.initialAgent]);
    setColumnMessages({ [scenario.initialAgent]: [] });
    setEventIndex(0);
    setShowModal(null);
    setThinking(null);
    setActiveAgent(scenario.initialAgent);
    setMessageCount(0);
    addTimer(() => setEventIndex(1), 100);
    return () => timerRefs.current.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioId]);

  // Process events
  useEffect(() => {
    if (eventIndex > 0 && scenario && eventIndex <= scenario.events.length) {
      processEventRef.current(eventIndex - 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventIndex]);

  if (!scenario) return null;

  const firstAgent = columns[0];
  const firstAgentMessages = columnMessages[firstAgent] || [];

  return (
    <div className="flex flex-col h-full" data-testid="adhoc-chat-view">
      <style>{`
        @keyframes adHocFade { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes modalIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
      `}</style>

      {/* Header */}
      <div className={`flex items-center gap-3 px-5 py-3 border-b ${divider} flex-shrink-0`}>
        <button onClick={onBack} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${isDark ? 'hover:bg-white/[0.06] text-white/60 hover:text-white' : 'hover:bg-black/[0.04] text-slate-500 hover:text-slate-800'}`} data-testid="adhoc-back">
          <ArrowLeft className="w-3.5 h-3.5" /> {backLabel || (externalScenarios ? 'Back to Org Pulse' : 'Back to Briefing')}
        </button>
        <div className={`h-4 w-px ${divider}`} />
        <div className="flex items-center gap-1.5">
          {columns.map(a => (
            <img key={a} src={AGENT_THUMBS[a]} alt="" className="w-5 h-5 rounded-full object-cover border border-white/20" />
          ))}
        </div>
        <span className={`text-sm font-semibold ${textPrimary} transition-all duration-300`} data-testid="adhoc-title">{headerTitle}</span>
        <div className="flex-1" />
        <span className={`text-[10px] px-2.5 py-1 rounded-full ${isDark ? 'bg-emerald-500/15 text-emerald-400' : 'bg-emerald-500/10 text-emerald-600'}`}>
          {columns.length} agent{columns.length > 1 ? 's' : ''} active
        </span>
      </div>

      {/* Content area — single-column (no card) vs multi-column (cards) */}
      {isSingleColumn ? (
        /* ─── Single agent: no card wrapper, messages restricted to chat input width ─── */
        <div className="flex-1 overflow-y-auto" data-testid="adhoc-single-view">
          <div className="w-[80%] mx-auto py-6">
            {firstAgentMessages.map((msg, i) => (
              <MessageBoundary key={i} msgType={msg?.type}>
                <ColumnMessage msg={msg} isDark={isDark} isMultiColumn={false} onCtaClick={handleCtaClick} />
              </MessageBoundary>
            ))}
            <div ref={singleScrollRef} />
          </div>
        </div>
      ) : (
        /* ─── Multi-agent: card-based grid ─── */
        <div className="flex-1 min-h-0 overflow-hidden px-3 py-3">
          <div className="grid gap-3 h-full" style={{ gridTemplateColumns: `repeat(${columns.length}, 1fr)` }}>
            {columns.map(agent => (
              <AgentCard
                key={agent}
                agent={agent}
                messages={columnMessages[agent] || []}
                isDark={isDark}
                isThinking={thinking === agent}
                isActiveChat={activeAgent === agent}
                columnCount={columns.length}
                onCtaClick={handleCtaClick}
              />
            ))}
          </div>
        </div>
      )}

      {/* Modal overlay for scenario C */}
      {showModal && (
        <div className="absolute inset-0 flex items-center justify-center z-50" style={{ background: isDark ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.2)' }}>
          <div className={`${solidBg} border ${divider} rounded-2xl p-6 shadow-xl max-w-sm`} style={{ animation: 'modalIn 0.2s ease-out' }} data-testid="loop-modal">
            <div className="flex items-center gap-3 mb-4">
              <img src={AGENT_THUMBS[showModal]} alt="" className="w-8 h-8 rounded-full object-cover" />
              <p className={`text-[14px] font-semibold ${textPrimary}`}>{AGENT_NAMES[showModal]}</p>
            </div>
            <p className={`text-[13px] leading-relaxed mb-5 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
              You're in an active conversation. Would you like to loop {AGENT_NAMES[showModal]} into this conversation or start a new chat?
            </p>
            <div className="flex items-center gap-2.5">
              <button
                onClick={() => { setShowModal(null); }}
                className="flex-1 px-4 py-2.5 rounded-xl text-[12px] font-medium bg-gradient-to-r from-indigo-500 to-purple-600 text-white"
                data-testid="modal-loop-in"
              >
                Loop into conversation
              </button>
              <button className={`flex-1 px-4 py-2.5 rounded-xl text-[12px] font-medium border ${divider} ${textSecondary}`}>
                New chat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chat bar — always visible */}
      <div className="flex justify-center pb-4 px-4 pt-2 flex-shrink-0">
        <div className="w-[80%]">
          <div className={`${solidBg} border ${divider} rounded-2xl flex flex-col`}>
            <div className="px-4 pt-3 pb-2">
              <div className={`w-full bg-transparent text-sm ${textSecondary}`}>
                Message {AGENT_NAMES[activeAgent]}...
              </div>
            </div>
            <div className="flex items-center justify-between px-3 pb-3">
              <div className="flex items-center gap-1">
                {columns.map(agent => {
                  const isSelected = activeAgent === agent;
                  return (
                    <button
                      key={agent}
                      onClick={() => setActiveAgent(agent)}
                      className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${isSelected ? (isDark ? 'bg-white/[0.08] ring-1 ring-indigo-500/40' : 'bg-indigo-50 ring-1 ring-indigo-300/60') : hoverBg}`}
                      data-testid={`adhoc-agent-${agent}`}
                    >
                      <img src={AGENT_THUMBS[agent]} alt="" className="w-5 h-5 rounded-full object-cover" />
                      <span className={`text-xs font-medium ${isSelected ? textPrimary : textSecondary}`}>{AGENT_NAMES[agent]}</span>
                    </button>
                  );
                })}
                <button className={`p-1.5 rounded-lg transition-colors ${hoverBg}`}>
                  <Paperclip className={`w-3.5 h-3.5 ${textSecondary}`} />
                </button>
              </div>
              <div className="flex items-center gap-1.5">
                <button className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${hoverBg}`}>
                  <Mic className={`w-4 h-4 ${textSecondary}`} />
                </button>
                <button className={`w-8 h-8 rounded-xl flex items-center justify-center ${isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]'}`}>
                  <Send className={`w-3.5 h-3.5 ${textSecondary}`} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default V4AdHocChat;
